import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import WorkerSearch from './pages/WorkerSearch';
import WorkerProfile from './pages/WorkerProfile';
import JobDetails from './pages/JobDetails';
import Wallet from './pages/Wallet';
import Profile from './pages/Profile';
import PublishJob from './pages/PublishJob';
import BrowseJobs from './pages/BrowseJobs';
import AdminPanel from './pages/AdminPanel';
import ChatList from './pages/ChatList';
import Chat from './pages/Chat';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GoogleAuthCallback({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate('/', { replace: true }); return; }
    const sessionId = decodeURIComponent(match[1]);
    // Clean the hash immediately
    window.history.replaceState(null, '', window.location.pathname);
    let role = 'client';
    try { role = sessionStorage.getItem('pending_google_role') || 'client'; } catch (_) {}
    axios.post(`${BACKEND_URL}/api/auth/google/session`, { session_id: sessionId, default_role: role })
      .then(res => {
        const { access_token, user, is_new_user } = res.data;
        onLogin(access_token, user);
        toast.success(is_new_user ? '¡Cuenta creada con Google!' : '¡Bienvenido de vuelta!');
        try { sessionStorage.removeItem('pending_google_role'); } catch (_) {}
        navigate('/dashboard', { replace: true });
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'No se pudo iniciar sesión con Google');
        navigate('/auth', { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8935] mx-auto mb-4"></div>
        <p className="text-slate-600">Iniciando sesión con Google...</p>
      </div>
    </div>
  );
}

function AppRouter({ user, token, handleLogin, handleLogout, setUser }) {
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <GoogleAuthCallback onLogin={handleLogin} />;
  }
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
      <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <Auth onLogin={handleLogin} />} />
      <Route path="/dashboard" element={user ? <Dashboard user={user} token={token} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/admin" element={user ? <AdminPanel user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/publish-job" element={user ? <PublishJob user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/browse-jobs" element={user ? <BrowseJobs user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/workers" element={user ? <WorkerSearch user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/worker/:workerId" element={user ? <WorkerProfile user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/job/:jobId" element={user ? <JobDetails user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/wallet" element={user ? <Wallet user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/profile" element={user ? <Profile user={user} token={token} setUser={setUser} /> : <Navigate to="/" />} />
      <Route path="/chats" element={user ? <ChatList user={user} token={token} /> : <Navigate to="/" />} />
      <Route path="/chat/:conversationId" element={user ? <Chat user={user} token={token} /> : <Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleLogin = (token, user) => {
    setToken(token);
    setUser(user);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter user={user} token={token} handleLogin={handleLogin} handleLogout={handleLogout} setUser={setUser} />
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
